import React, { useState, useCallback, useEffect } from 'react';
import { DndProvider, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import PlanCard from './PlanCard';
import EditableField from './EditableField';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';

// Drop zone component for empty spaces
const DropZone = ({ dataSetIndex, movePlan, disableDrag }) => {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: "plan",
    drop: (draggedItem) => {
      if (disableDrag) return;
      // Only allow drops from different columns
      if (draggedItem.dataSetIndex !== dataSetIndex) {
        movePlan(draggedItem.tierIndex, 0, draggedItem.dataSetIndex, dataSetIndex, true);
      }
    },
    canDrop: (draggedItem) => {
      if (disableDrag) return false;
      // Only allow drops from different columns
      return draggedItem.dataSetIndex !== dataSetIndex;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const getDropZoneStyles = () => {
    let styles = 'border-2 border-dashed rounded-md p-4 transition-all duration-200 min-h-[60px] flex items-center justify-center';

    if (disableDrag) {
      styles += ' border-gray-200 bg-gray-50';
    } else {
      styles += ' border-gray-300 bg-gray-50 hover:bg-gray-100';
    }

    if (isOver && canDrop) {
      styles += ' ring-2 ring-blue-400 ring-opacity-75 bg-blue-50 border-blue-400';
    } else if (isOver && !canDrop) {
      styles += ' ring-2 ring-red-400 ring-opacity-75 bg-red-50 border-red-400';
    }

    return styles;
  };

  return (
    <div ref={drop} className={getDropZoneStyles()}>
      {!disableDrag && <span className="text-xs text-gray-400">Drop here to add to this column</span>}
    </div>
  );
};

const PlanOrganizer = ({ planData, keyDifferences, loading, reloadingIds, setPlansData, handleReprocess, handleCompare, handleRestore, consultantCompensation }) => {
  const [expandedTiers, setExpandedTiers] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  const [readKeyDifferences, setReadKeyDifferences] = useState([]);
  const [editingField, setEditingField] = useState(null); // Format: 'tierIndex-dataSetIndex-fieldPath'
  const [editValue, setEditValue] = useState('');
  const [tempPlanData, setTempPlanData] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Force re-render when enrollment data changes
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [planData]);

  useEffect(() => {
    const newReadKeyDifferences = [];
    if (keyDifferences) {
      for (let i = 0; i < planData.length; i++) {
        toggleTier(i);
        newReadKeyDifferences.push(false);
      }
      setReadKeyDifferences(newReadKeyDifferences);
    }
  }, [keyDifferences]);

  const toggleTier = (tierIndex) => {
    setExpandedTiers(prev => ({
      ...prev,
      [tierIndex]: !prev[tierIndex]
    }));
  };

  const toggleSection = (tierIndex, sectionName) => {
    const key = `${tierIndex}-${sectionName}`;
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const movePlan = useCallback((fromTier, toTier, fromIndex, toIndex, isEmptySpace = false) => {
    setPlansData(prevData => {
      // Deep clone the entire data structure to ensure React detects changes
      const newData = prevData.map((dataset, idx) => {
        // Only deep clone the datasets we're modifying
        if (idx === fromIndex || idx === toIndex) {
          return {
            ...dataset,
            plans: dataset.plans.map(plan => plan ? { ...plan } : plan)
          };
        }
        return dataset;
      });

      if (isEmptySpace) {
        // Moving to empty space - no swap, just move
        const planToMove = newData[fromIndex].plans[fromTier];
        
        // Create new array references for React to detect changes
        newData[fromIndex] = {
          ...newData[fromIndex],
          plans: [...newData[fromIndex].plans]
        };
        
        // Remove from source
        newData[fromIndex].plans.splice(fromTier, 1);
        
        // Re-number tiers in source column
        newData[fromIndex].plans = newData[fromIndex].plans.map((plan, index) => ({
          ...plan,
          tier: index + 1
        }));
        
        // Create new array reference for target if different column
        if (fromIndex !== toIndex) {
          newData[toIndex] = {
            ...newData[toIndex],
            plans: [...newData[toIndex].plans]
          };
        }
        
        // Add to target
        newData[toIndex].plans.push({ ...planToMove, tier: newData[toIndex].plans.length + 1 });
      } else {
        // Swap mode (existing behavior)
        const planA = newData[fromIndex].plans[fromTier];
        const planB = newData[toIndex].plans[toTier];

        // Create new array references for React to detect changes
        newData[fromIndex] = {
          ...newData[fromIndex],
          plans: [...newData[fromIndex].plans]
        };
        
        if (fromIndex !== toIndex) {
          newData[toIndex] = {
            ...newData[toIndex],
            plans: [...newData[toIndex].plans]
          };
        }

        // Swap the plans (works for vertical, horizontal, and diagonal movements)
        newData[fromIndex].plans[fromTier] = planB;
        newData[toIndex].plans[toTier] = planA;
      }

      return newData;
    });
  }, []);

  const deletePlan = useCallback((tierIndex, dataSetIndex) => {
    setPlansData(prevData => {
      const newData = [...prevData];
      
      // Create new dataset object with new plans array
      newData[dataSetIndex] = {
        ...newData[dataSetIndex],
        plans: newData[dataSetIndex].plans
          .filter((plan) => plan.tier !== tierIndex + 1)
          .map((plan, index) => ({
            ...plan,
            tier: index + 1
          }))
      };
      
      return newData;
    });
  }, []);

  const getTypeBadge = (type, index) => {
    const colors = {
      current: 'bg-blue-100 text-blue-800',
      renewal: 'bg-green-100 text-green-800',
      alternative: [
        'bg-yellow-100 text-yellow-800',
        'bg-orange-100 text-orange-800',
        'bg-purple-100 text-purple-800',
        'bg-teal-100 text-teal-800',
        'bg-pink-100 text-pink-800'
      ]
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium uppercase ${type !== 'alternative' ? colors[type] : colors.alternative[(index - 2) % 5] || 'bg-gray-100 text-gray-800'}`}>
        {type.charAt(0).toUpperCase() + type.slice(1)} {type === 'alternative' ? index - 1 : ''}
      </span>
    );
  };

  if (!planData || planData.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-gray-500 text-lg">No plan data available</p>
      </div>
    );
  }

  // Get the maximum number of plans across all sets
  const maxPlans = Math.max(...planData.map(set => set.plans.length));

  // Filter tiers based on results when present
  const visibleTiers = keyDifferences
    ? Array.from({ length: maxPlans }).filter((_, tierIndex) => {
      const tierDiff = keyDifferences?.find(diff => diff.tier === tierIndex + 1);
      return tierDiff && tierDiff.comparable;
    })
    : Array.from({ length: maxPlans });

  const hasTierUnmatchingEnrollment = (tierIndex) => {
    if (!planData || planData.length < 2) return false;

    // Compare each value with others to find any differences
    for (let i = 0; i < planData.length; i++) {
      for (let j = i + 1; j < planData.length; j++) {
        if (planData[i].plans[tierIndex]?.enrollment?.individual !== planData[j].plans[tierIndex]?.enrollment?.individual ||
          planData[i].plans[tierIndex]?.enrollment?.individualAndSpouse !== planData[j].plans[tierIndex]?.enrollment?.individualAndSpouse ||
          planData[i].plans[tierIndex]?.enrollment?.individualAndChildren !== planData[j].plans[tierIndex]?.enrollment?.individualAndChildren ||
          planData[i].plans[tierIndex]?.enrollment?.family !== planData[j].plans[tierIndex]?.enrollment?.family) {
          return true;
        }
      }
    }

    return false;
  }

  const replaceTierString = (string) => {
    string = string.replace('Tier1', 'Generic Drugs');
    string = string.replace('Tier2', 'Higher Priced Generics / Lower Priced Brand name');
    string = string.replace('Tier3', 'Brand Name');
    string = string.replace('Tier4', 'Highest Cost');
    string = string.replace('Retail ', '');
    return string;
  }

  // Calculate consultant compensation for a dataset
  const calculateConsultantCompensation = (dataSet) => {
    if (!consultantCompensation || dataSet.type === 'current') return null;

    const carrierValue = consultantCompensation.values[dataSet.carrier];
    if (!carrierValue) return null;

    if (consultantCompensation.model === 'per_employee') {
      // Calculate total enrolled for this dataset
      const totalEnrolled = dataSet.plans.reduce((sum, plan) => {
        const enrollment = plan.enrollment || {};
        return sum + (enrollment.individual || 0) + (enrollment.individualAndSpouse || 0) +
          (enrollment.individualAndChildren || 0) + (enrollment.family || 0);
      }, 0);

      return totalEnrolled * carrierValue * 12; // Monthly rate * 12 months
    } else if (consultantCompensation.model === 'percentage') {
      // Calculate percentage of total annual premium
      const totalAnnual = dataSet.totalAnnual || 0;
      return totalAnnual * (carrierValue / 100);
    }

    return null;
  };

  // Check if consultant compensation values are missing for any carrier
  const hasIncompleteConsultantCompensation = () => {
    if (!consultantCompensation) return true;

    const renewalAndAlternativeCarriers = new Set();
    planData.forEach(dataset => {
      if (dataset.type !== 'current') {
        renewalAndAlternativeCarriers.add(dataset.carrier);
      }
    });

    return Array.from(renewalAndAlternativeCarriers).some(carrier =>
      !consultantCompensation.values[carrier]
    );
  };

  // Helper function to get nested value from object using dot notation path
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  };

  // Helper function to set nested value in object using dot notation path
  const setNestedValue = (obj, path, value) => {
    const keys = path.split('.');
    const lastKey = keys.pop();
    const target = keys.reduce((current, key) => {
      if (!current[key]) current[key] = {};
      return current[key];
    }, obj);
    target[lastKey] = value;
  };

  // Start editing a field
  const startEditing = (tierIndex, dataSetIndex, fieldPath) => {
    if (keyDifferences) return; // Don't allow editing when comparing

    const fieldKey = `${tierIndex}-${dataSetIndex}-${fieldPath}`;
    const currentValue = getNestedValue(planData[dataSetIndex].plans[tierIndex], fieldPath);

    // For rates fields, show the raw numeric value for editing
    let editableValue = '';
    if (fieldPath.startsWith('rates.') && currentValue !== null && currentValue !== undefined) {
      editableValue = currentValue.toString();
    } else {
      editableValue = currentValue || '';
    }

    setEditingField(fieldKey);
    setEditValue(editableValue);
    setTempPlanData(JSON.parse(JSON.stringify(planData))); // Deep copy for potential rollback
  };

  // Save the edited value
  const saveEdit = () => {
    if (!editingField) return;

    const [tierIndex, dataSetIndex, fieldPath] = editingField.split('-');
    const newPlanData = [...planData];

    // Create a deep copy of the plan to modify
    if (!newPlanData[dataSetIndex].plans[tierIndex]) {
      newPlanData[dataSetIndex].plans[tierIndex] = {};
    }

    // Convert to number if it's a rates field
    let valueToSave = editValue;
    if (fieldPath.startsWith('rates.')) {
      const numericValue = parseFloat(editValue);
      valueToSave = isNaN(numericValue) ? null : numericValue;
    }

    setNestedValue(newPlanData[dataSetIndex].plans[tierIndex], fieldPath, valueToSave);

    setPlansData(newPlanData);
    cancelEdit();
  };

  // Cancel editing and revert to original value
  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
    setTempPlanData(null);
  };

  const renderEditableField = (
    tierIndex,
    dataSetIndex,
    fieldPath,
    value,
    isNumeric = false,
    isMoney = false,
    className = ""
  ) => (
    <EditableField
      tierIndex={tierIndex}
      dataSetIndex={dataSetIndex}
      fieldPath={fieldPath}
      value={value} // pass raw number, not formatted string
      className={className}
      isNumeric={isNumeric}
      isMoney={isMoney}
      editingField={editingField}
      editValue={editValue}
      setEditValue={setEditValue}
      onStartEdit={startEditing}
      onSave={saveEdit}
      onCancel={cancelEdit}
      disabled={!!keyDifferences || reloadingIds.includes(planData[dataSetIndex].id)}
    />
  );

  return (
    <div className="mb-6">
      <DndProvider backend={HTML5Backend}>
        <div className="mb-4">
          <h2 className="text-xl font-semibold mb-2">Compare Plans</h2>
          {!keyDifferences ? (
            <div className="text-xs text-gray-500 mt-1 space-y-1">
              <div>Drag and drop plans to swap their positions.</div>
              <div>Click on benefit values and rates to edit them inline.</div>
            </div>
          ) : (
            <span className="text-xs text-gray-500 mt-1 block">
              Editing is disabled during plan comparison.
            </span>
          )}

          <div className="mt-4 overflow-x-auto overflow-y-visible">
            <table className="divide-y divide-gray-200 table-fixed whitespace-pre-line" style={{ width: 'auto' }}>
              <colgroup>
                <col style={{ width: '170px' }} />
                {planData.map((_, index) => (
                  <col key={index} style={{ width: '270px' }} />
                ))}
              </colgroup>
              <tbody key={refreshKey} className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-1 py-1 text-sm font-medium text-gray-900">Carrier/Type</td>
                  {planData.map((dataSet, index) => (
                    <td key={index} className="px-1 py-1 text-sm font-medium text-gray-900 overflow-hidden">
                      <div className="flex flex-row gap-2 items-center justify-between">
                        <span>{dataSet.carrier}</span>
                        {getTypeBadge(dataSet.type, index)}
                      </div>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="px-1 py-1 text-sm font-medium text-gray-900">File Name</td>
                  {planData.map((dataSet, index) => {
                    if (dataSet.type === "current" && planData.find(otherDataSet => otherDataSet.type === "renewal")?.fileName === dataSet.fileName && planData.find(otherDataSet => otherDataSet.type === "renewal")?.fileId === dataSet.fileId) {
                      return (
                        <td key={index} colSpan={2} className="px-1 py-1 text-sm font-medium text-gray-900 overflow-hidden">
                          <div className="flex justify-start items-center">
                            {!keyDifferences && (
                              <button
                                title="Reprocess file"
                                disabled={reloadingIds.includes(dataSet.id)}
                                onClick={() =>
                                  handleReprocess(
                                    [dataSet.id, planData.find(otherDataSet => otherDataSet.type === "renewal")?.id],
                                    dataSet.fileId,
                                    dataSet.fileName
                                  )
                                }
                                className="text-xs text-blue-500 hover:text-blue-700"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={1.5}
                                  stroke="currentColor"
                                  className={`w-4 h-4 inline-block mr-1 ${reloadingIds.includes(dataSet.id) ? "animate-spin" : ""}`}
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                                </svg>
                              </button>
                            )}
                            <span className="text-xs text-gray-500">{dataSet.fileName}</span>
                          </div>
                        </td>
                      )
                    } else if (dataSet.type === "renewal" && planData.find(otherDataSet => otherDataSet.type === "current")?.fileName === dataSet.fileName && planData.find(otherDataSet => otherDataSet.type === "current")?.fileId === dataSet.fileId) {
                      return;
                    } else {
                      return (
                        <td key={index} className="px-1 py-1 text-sm font-medium text-gray-900 overflow-hidden">
                          <div className="flex justify-start items-center">
                            {!keyDifferences && (
                              <button
                              title="Reprocess file"
                              disabled={reloadingIds.includes(dataSet.id)}
                              onClick={() =>
                                handleReprocess(
                                  [dataSet.id],
                                  dataSet.fileId,
                                  dataSet.fileName
                                )
                              }
                              className="text-xs text-blue-500 hover:text-blue-700"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className={`w-4 h-4 inline-block mr-1 ${reloadingIds.includes(dataSet.id) ? "animate-spin" : ""}`}
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                              </svg>
                            </button>
                            )}
                            <span className="text-xs text-gray-500">{dataSet.fileName}</span>
                          </div>
                        </td>
                      )
                    }
                  })}
                </tr>
                {visibleTiers.map((_, tierIndex) => (
                  <React.Fragment key={tierIndex}>
                    <tr onClick={() => toggleTier(tierIndex)} className="cursor-pointer">
                      <td className="px-1 py-1 text-sm font-medium text-gray-900">
                        <div className="flex items-center">
                          {expandedTiers[tierIndex] ? (
                            <ChevronUpIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                          ) : (
                            <ChevronDownIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                          )}
                          <h3 className="ml-1 whitespace-nowrap">
                            {hasTierUnmatchingEnrollment(tierIndex) && (
                              <div className="relative group inline-block">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-yellow-500 inline-block mr-1">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                </svg>
                                <div className="absolute hidden group-hover:block bg-gray-700 text-white text-xs rounded px-2 py-1 -mt-1 left-6 z-50">
                                  The enrollment values for this option are different across the datasets.
                                </div>
                              </div>
                            )}
                            Option {tierIndex + 1}
                          </h3>
                        </div>
                      </td>
                      {planData.map((dataSet, index) => (
                        <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                          {dataSet.plans[tierIndex] && (
                            <PlanCard
                              plan={dataSet.plans[tierIndex]}
                              tierIndex={tierIndex}
                              dataSetIndex={index}
                              movePlan={movePlan}
                              deletePlan={deletePlan}
                              carrier={dataSet.carrier}
                              type={dataSet.type}
                              fileName={dataSet.fileName}
                              disableDrag={!!keyDifferences || reloadingIds.includes(dataSet.id)}
                              onDragStart={() => setIsDragging(true)}
                              onDragEnd={() => setIsDragging(false)}
                            />
                          )}
                        </td>
                      ))}
                    </tr>

                    {expandedTiers[tierIndex] && (
                      <>
                        {/* Benefits Section */}
                        <tr onClick={() => toggleSection(tierIndex, 'benefits')} className="cursor-pointer bg-gray-200 hover:bg-gray-300">
                          <td colSpan={planData.length + 1} className="px-1 py-1 text-sm font-bold text-gray-800">
                            <div className="flex items-center">
                              {expandedSections[`${tierIndex}-benefits`] ? (
                                <ChevronUpIcon className="h-4 w-4 text-gray-600 flex-shrink-0" />
                              ) : (
                                <ChevronDownIcon className="h-4 w-4 text-gray-600 flex-shrink-0" />
                              )}
                              <span className="ml-1">Benefits</span>
                            </div>
                          </td>
                        </tr>

                        {expandedSections[`${tierIndex}-benefits`] && (
                          <>
                            {/* In-Network Benefits */}
                            <tr className="bg-gray-100">
                              <td colSpan={planData.length + 1} className="px-6 py-1 text-sm font-medium text-gray-800">
                                In-Network
                              </td>
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Deductible
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "benefits.inNetwork.deductible",
                                    dataSet.plans[tierIndex]?.benefits?.inNetwork?.deductible
                                  )}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Primary Care Visit
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "benefits.inNetwork.officeVisitsPrimaryCare",
                                    dataSet.plans[tierIndex]?.benefits?.inNetwork?.officeVisitsPrimaryCare
                                  )}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Specialty Visit
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "benefits.inNetwork.officeVisitsSpecialty",
                                    dataSet.plans[tierIndex]?.benefits?.inNetwork?.officeVisitsSpecialty
                                  )}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Inpatient Hospital
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "benefits.inNetwork.inpatientHospital",
                                    dataSet.plans[tierIndex]?.benefits?.inNetwork?.inpatientHospital
                                  )}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Emergency Room
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "benefits.inNetwork.emergencyRoom",
                                    dataSet.plans[tierIndex]?.benefits?.inNetwork?.emergencyRoom
                                  )}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Urgent Care
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "benefits.inNetwork.urgentCare",
                                    dataSet.plans[tierIndex]?.benefits?.inNetwork?.urgentCare
                                  )}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Lab
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "benefits.inNetwork.lab",
                                    dataSet.plans[tierIndex]?.benefits?.inNetwork?.lab
                                  )}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                X-Ray
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "benefits.inNetwork.xray",
                                    dataSet.plans[tierIndex]?.benefits?.inNetwork?.xray
                                  )}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Coinsurance
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "benefits.inNetwork.coinsurance",
                                    dataSet.plans[tierIndex]?.benefits?.inNetwork?.coinsurance
                                  )}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Out of Pocket Max
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "benefits.inNetwork.outOfPocket",
                                    dataSet.plans[tierIndex]?.benefits?.inNetwork?.outOfPocket
                                  )}
                                </td>
                              ))}
                            </tr>

                            {/* Out-of-Network Benefits */}
                            <tr className="bg-gray-100">
                              <td colSpan={planData.length + 1} className="px-6 py-1 text-sm font-medium text-gray-800">
                                Out-of-Network
                              </td>
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Deductible
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "benefits.outOfNetwork.deductible",
                                    dataSet.plans[tierIndex]?.benefits?.outOfNetwork?.deductible
                                  )}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Coinsurance
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "benefits.outOfNetwork.coinsurance",
                                    dataSet.plans[tierIndex]?.benefits?.outOfNetwork?.coinsurance
                                  )}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Out of Pocket Max
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "benefits.outOfNetwork.outOfPocket",
                                    dataSet.plans[tierIndex]?.benefits?.outOfNetwork?.outOfPocket
                                  )}
                                </td>
                              ))}
                            </tr>

                            {/* Pharmacy Benefits */}
                            <tr className="bg-gray-100">
                              <td colSpan={planData.length + 1} className="px-6 py-1 text-sm font-medium text-gray-800">
                                Pharmacy
                              </td>
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Generic Drugs
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "benefits.pharmacy.retailTier1",
                                    dataSet.plans[tierIndex]?.benefits?.pharmacy?.retailTier1
                                  )}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Higher Priced Generics / Lower Priced Brand name
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "benefits.pharmacy.retailTier2",
                                    dataSet.plans[tierIndex]?.benefits?.pharmacy?.retailTier2
                                  )}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Brand Name
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "benefits.pharmacy.retailTier3",
                                    dataSet.plans[tierIndex]?.benefits?.pharmacy?.retailTier3
                                  )}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Highest Cost
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "benefits.pharmacy.retailTier4",
                                    dataSet.plans[tierIndex]?.benefits?.pharmacy?.retailTier4
                                  )}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Deductible
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "benefits.pharmacy.deductible",
                                    dataSet.plans[tierIndex]?.benefits?.pharmacy?.deductible
                                  )}
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Out of Pocket Max
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "benefits.pharmacy.outOfPocket",
                                    dataSet.plans[tierIndex]?.benefits?.pharmacy?.outOfPocket
                                  )}
                                </td>
                              ))}
                            </tr>

                            {/* Notes */}
                            <tr className="bg-gray-100">
                              <td colSpan={planData.length + 1} className="px-6 py-1 text-sm font-medium text-gray-800">
                                Other benefits
                              </td>
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900"></td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "notes",
                                    dataSet.plans[tierIndex]?.notes
                                  )}
                                </td>
                              ))}
                            </tr>
                          </>
                        )}

                        {/* Enrollment & Rates Section */}
                        <tr onClick={() => toggleSection(tierIndex, 'enrollment')} className="cursor-pointer bg-gray-200 hover:bg-gray-300">
                          <td colSpan={planData.length + 1} className="px-1 py-1 text-sm font-bold text-gray-800">
                            <div className="flex items-center">
                              {expandedSections[`${tierIndex}-enrollment`] ? (
                                <ChevronUpIcon className="h-4 w-4 text-gray-600 flex-shrink-0" />
                              ) : (
                                <ChevronDownIcon className="h-4 w-4 text-gray-600 flex-shrink-0" />
                              )}
                              <span className="ml-1">Enrollment & Rates</span>
                            </div>
                          </td>
                        </tr>

                        {expandedSections[`${tierIndex}-enrollment`] && (
                          <>
                            {/* Enrollment */}
                            <tr className="bg-gray-100">
                              <td colSpan={planData.length + 1} className="px-6 py-1 text-sm font-medium text-gray-800">
                                Enrollment
                              </td>
                            </tr>
                            <tr key={`individual-enrollment-${tierIndex}-${JSON.stringify(planData.map(d => d.plans[tierIndex]?.enrollment?.individual))}`}>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Individual
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={`${index}-${dataSet.plans[tierIndex]?.enrollment?.individual}`} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {dataSet.plans[tierIndex]?.enrollment?.individual}
                                </td>
                              ))}
                            </tr>
                            <tr key={`spouse-enrollment-${tierIndex}-${JSON.stringify(planData.map(d => d.plans[tierIndex]?.enrollment?.individualAndSpouse))}`}>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Individual + Spouse
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={`${index}-${dataSet.plans[tierIndex]?.enrollment?.individualAndSpouse}`} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {dataSet.plans[tierIndex]?.enrollment?.individualAndSpouse}
                                </td>
                              ))}
                            </tr>
                            <tr key={`children-enrollment-${tierIndex}-${JSON.stringify(planData.map(d => d.plans[tierIndex]?.enrollment?.individualAndChildren))}`}>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Individual + Children
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={`${index}-${dataSet.plans[tierIndex]?.enrollment?.individualAndChildren}`} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {dataSet.plans[tierIndex]?.enrollment?.individualAndChildren}
                                </td>
                              ))}
                            </tr>
                            <tr key={`family-enrollment-${tierIndex}-${JSON.stringify(planData.map(d => d.plans[tierIndex]?.enrollment?.family))}`}>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Family
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={`${index}-${dataSet.plans[tierIndex]?.enrollment?.family}`} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {dataSet.plans[tierIndex]?.enrollment?.family}
                                </td>
                              ))}
                            </tr>

                            {/* Rates */}

                            <tr className="bg-gray-100">
                              <td
                                colSpan={planData.length + 1}
                                className="px-6 py-1 text-sm font-medium text-gray-800"
                              >
                                Rates
                              </td>
                            </tr>

                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Individual
                              </td>
                              {planData.map((dataSet, index) => (
                                <td
                                  key={index}
                                  className="px-1 py-1 text-sm text-gray-500 overflow-hidden"
                                >
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "rates.individual",
                                    dataSet.plans[tierIndex]?.rates.individual,
                                    true,
                                    true
                                  )}
                                </td>
                              ))}
                            </tr>

                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Individual + Spouse
                              </td>
                              {planData.map((dataSet, index) => (
                                <td
                                  key={index}
                                  className="px-1 py-1 text-sm text-gray-500 overflow-hidden"
                                >
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "rates.individualAndSpouse",
                                    dataSet.plans[tierIndex]?.rates.individualAndSpouse,
                                    true,
                                    true
                                  )}
                                </td>
                              ))}
                            </tr>

                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Individual + Children
                              </td>
                              {planData.map((dataSet, index) => (
                                <td
                                  key={index}
                                  className="px-1 py-1 text-sm text-gray-500 overflow-hidden"
                                >
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "rates.individualAndChildren",
                                    dataSet.plans[tierIndex]?.rates.individualAndChildren,
                                    true,
                                    true
                                  )}
                                </td>
                              ))}
                            </tr>

                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Family
                              </td>
                              {planData.map((dataSet, index) => (
                                <td
                                  key={index}
                                  className="px-1 py-1 text-sm text-gray-500 overflow-hidden"
                                >
                                  {renderEditableField(
                                    tierIndex,
                                    index,
                                    "rates.family",
                                    dataSet.plans[tierIndex]?.rates.family,
                                    true,
                                    true
                                  )}
                                </td>
                              ))}
                            </tr>

                            {/* Calculated Monthly Costs */}
                            <tr className="bg-gray-100">
                              <td colSpan={planData.length + 1} className="px-6 py-1 text-sm font-medium text-gray-800">
                                Monthly Costs
                              </td>
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Individual
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {dataSet.plans[tierIndex]?.costs?.individual !== undefined &&
                                    dataSet.plans[tierIndex]?.costs?.individual !== null &&
                                    new Intl.NumberFormat('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                      minimumFractionDigits: 2
                                    }).format(dataSet.plans[tierIndex].costs.individual)
                                  }
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Individual + Spouse
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {dataSet.plans[tierIndex]?.costs?.individualAndSpouse !== undefined &&
                                    dataSet.plans[tierIndex]?.costs?.individualAndSpouse !== null &&
                                    new Intl.NumberFormat('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                      minimumFractionDigits: 2
                                    }).format(dataSet.plans[tierIndex].costs.individualAndSpouse)
                                  }
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Individual + Children
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {dataSet.plans[tierIndex]?.costs?.individualAndChildren !== undefined &&
                                    dataSet.plans[tierIndex]?.costs?.individualAndChildren !== null &&
                                    new Intl.NumberFormat('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                      minimumFractionDigits: 2
                                    }).format(dataSet.plans[tierIndex].costs.individualAndChildren)
                                  }
                                </td>
                              ))}
                            </tr>
                            <tr>
                              <td className="px-1 py-1 text-sm font-medium text-gray-900">
                                Family
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm text-gray-500 overflow-hidden">
                                  {dataSet.plans[tierIndex]?.costs?.family !== undefined &&
                                    dataSet.plans[tierIndex]?.costs?.family !== null &&
                                    new Intl.NumberFormat('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                      minimumFractionDigits: 2
                                    }).format(dataSet.plans[tierIndex].costs.family)
                                  }
                                </td>
                              ))}
                            </tr>

                            <tr className="bg-gray-100">
                              <td colSpan={planData.length + 1} className="px-6 py-1 text-sm font-medium text-gray-800">
                                Combined Costs
                              </td>
                            </tr>
                            <tr className="bg-gray-50">
                              <td className="px-1 py-1 text-sm font-bold text-gray-900">
                                Monthly Cost
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm font-bold text-gray-900 overflow-hidden">
                                  {dataSet.plans[tierIndex]?.costs?.totalMonthly !== undefined &&
                                    dataSet.plans[tierIndex]?.costs?.totalMonthly !== null &&
                                    new Intl.NumberFormat('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                      minimumFractionDigits: 2
                                    }).format(dataSet.plans[tierIndex].costs.totalMonthly)
                                  }
                                </td>
                              ))}
                            </tr>
                            <tr className="bg-gray-100">
                              <td className="px-1 py-1 text-sm font-bold text-gray-900">
                                Annual Cost
                              </td>
                              {planData.map((dataSet, index) => (
                                <td key={index} className="px-1 py-1 text-sm font-bold text-gray-900 overflow-hidden">
                                  {dataSet.plans[tierIndex]?.costs?.totalAnnual !== undefined &&
                                    dataSet.plans[tierIndex]?.costs?.totalAnnual !== null &&
                                    new Intl.NumberFormat('en-US', {
                                      style: 'currency',
                                      currency: 'USD',
                                      minimumFractionDigits: 2
                                    }).format(dataSet.plans[tierIndex].costs.totalAnnual)
                                  }
                                </td>
                              ))}
                            </tr>
                            <tr className="bg-gray-100">
                              <td className="px-1 py-1 text-sm font-bold text-gray-900">
                                Increase over Current
                              </td>
                              {planData.map((dataSet, index) => {
                                if (dataSet.type === 'current') {
                                  return (
                                    <td key={index} className="px-1 py-1 text-sm text-gray-900 overflow-hidden">—</td>
                                  );
                                } else {
                                  return (
                                    <td key={index} className="px-1 py-1 text-sm font-bold text-gray-900 overflow-hidden">
                                      {dataSet.plans[tierIndex]?.costs?.increase !== undefined &&
                                        dataSet.plans[tierIndex]?.costs?.increase !== null &&
                                        new Intl.NumberFormat('en-US', {
                                          style: 'percent',
                                          minimumFractionDigits: 1
                                        }).format(dataSet.plans[tierIndex].costs.increase)
                                      }
                                    </td>
                                  );
                                }
                              })}
                            </tr>
                          </>
                        )}

                        {/* Key Differences Section */}
                        {keyDifferences?.find(diff => diff.tier === tierIndex + 1)?.differences && (
                          <>
                            <tr onClick={() => { toggleSection(tierIndex, 'differences'); readKeyDifferences[tierIndex] = true }} className="cursor-pointer bg-gray-200 hover:bg-gray-300">
                              <td colSpan={planData.length + 1} className="px-1 py-1 text-sm font-bold text-gray-800">
                                <div className="flex items-center">
                                  {expandedSections[`${tierIndex}-differences`] ? (
                                    <ChevronUpIcon className="h-4 w-4 text-gray-600 flex-shrink-0" />
                                  ) : (
                                    <ChevronDownIcon className="h-4 w-4 text-gray-600 flex-shrink-0" />
                                  )}
                                  <span className="ml-1">Key Differences</span>
                                  {!readKeyDifferences[tierIndex] && (
                                    <div className="ml-1 relative group inline-block text-sm">
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="red" viewBox="0 0 512 512" className="pb-1 w-3 h-3 text-red-500 inline-block">
                                        <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512z" />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>

                            {expandedSections[`${tierIndex}-differences`] && (
                              <tr>
                                <td colSpan={planData.length + 1} className="px-1 py-1">
                                  <div className="bg-gray-50 rounded-md p-2">
                                    {(() => {
                                      const tierDiff = keyDifferences?.find(diff => diff.tier === tierIndex + 1);
                                      const differences = tierDiff?.differences;

                                      return (
                                        <div className="text-sm text-gray-600 space-y-3">
                                          {/* In-Network Differences */}
                                          {differences?.inNetwork && differences.inNetwork.length > 0 && (
                                            <div>
                                              <h4 className="font-medium text-gray-900 mb-2">In-Network Benefits</h4>
                                              <div className="space-y-2">
                                                {differences.inNetwork.map((diff, index) => (
                                                  <div key={index} className="pl-2">
                                                    <span className="font-medium">{diff.property.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</span> {diff.difference}
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}

                                          {/* Out-of-Network Differences */}
                                          {differences?.outOfNetwork && differences.outOfNetwork.length > 0 && (
                                            <div>
                                              <h4 className="font-medium text-gray-900 mb-2">Out-of-Network Benefits</h4>
                                              <div className="space-y-2">
                                                {differences.outOfNetwork.map((diff, index) => (
                                                  <div key={index} className="pl-2">
                                                    <span className="font-medium">{diff.property.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</span> {diff.difference}
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}

                                          {/* Pharmacy Differences */}
                                          {differences?.pharmacy && differences.pharmacy.length > 0 && (
                                            <div>
                                              <h4 className="font-medium text-gray-900 mb-2">Pharmacy Benefits</h4>
                                              <div className="space-y-2">
                                                {differences.pharmacy.map((diff, index) => (
                                                  <div key={index} className="pl-2">
                                                    <span className="font-medium">{replaceTierString(diff.property.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()))}:</span> {diff.difference}
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </>
                        )}

                      </>
                    )}
                  </React.Fragment>
                ))}

                {/* Drop zones for adding plans to columns - only show when dragging */}
                {!keyDifferences && isDragging && (
                  <tr>
                    <td className="px-1 py-1 text-sm font-medium text-gray-900">
                      <div className="text-xs text-gray-500">Drop to move</div>
                    </td>
                    {planData.map((dataSet, index) => (
                      <td key={index} className="px-1 py-1">
                        <DropZone
                          dataSetIndex={index}
                          movePlan={movePlan}
                          disableDrag={!!keyDifferences || reloadingIds.includes(dataSet.id)}
                        />
                      </td>
                    ))}
                  </tr>
                )}

                <tr className="bg-gray-50">
                  <td className="px-1 py-1 text-sm font-bold text-gray-900">
                    Total Monthly Cost
                  </td>
                  {planData.map((dataSet, index) => (
                    <td key={index} className="px-1 py-1 text-sm font-bold text-gray-900 overflow-hidden">
                      {dataSet.totalMonthly !== undefined &&
                        dataSet.totalMonthly !== null &&
                        new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 2
                        }).format(dataSet.totalMonthly)
                      }
                    </td>
                  ))}
                </tr>
                <tr className="bg-gray-100">
                  <td className="px-1 py-1 text-sm font-bold text-gray-900">
                    Total Annual Cost
                  </td>
                  {planData.map((dataSet, index) => (
                    <td key={index} className="px-1 py-1 text-sm font-bold text-gray-900 overflow-hidden">
                      {dataSet.totalAnnual !== undefined &&
                        dataSet.totalAnnual !== null &&
                        new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 2
                        }).format(dataSet.totalAnnual)
                      }
                    </td>
                  ))}
                </tr>
                <tr className="bg-gray-100">
                  <td className="px-1 py-1 text-sm font-bold text-gray-900">
                    Total Increase
                  </td>
                  {planData.map((dataSet, index) => {
                    if (dataSet.type === 'current') {
                      return (
                        <td key={index} className="px-1 py-1 text-sm text-gray-900 overflow-hidden">—</td>
                      );
                    } else {
                      return (
                        <td key={index} className="px-1 py-1 text-sm font-bold text-gray-900 overflow-hidden">
                          {dataSet.increase !== undefined &&
                            dataSet.increase !== null &&
                            new Intl.NumberFormat('en-US', {
                              style: 'percent',
                              minimumFractionDigits: 1
                            }).format(dataSet.increase)
                          }
                        </td>
                      );
                    }
                  })}
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-1 py-1 text-sm font-bold text-gray-900">
                    <div className="flex items-center">
                      {hasIncompleteConsultantCompensation() && (
                        <div className="relative group inline-block mr-1">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-yellow-500 inline-block">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                          </svg>
                          <div className="absolute hidden group-hover:block bg-gray-700 text-white text-xs rounded px-2 py-1 -mt-1 left-6 z-50 whitespace-nowrap">
                            Consultant compensation values not set for all carriers
                          </div>
                        </div>
                      )}
                      Consultant Compensation (408(b)(2) Disclosure)
                    </div>
                  </td>
                  {planData.map((dataSet, index) => {
                    if (dataSet.type === 'current') {
                      return (
                        <td key={index} className="px-1 py-1 text-sm text-gray-900 overflow-hidden">—</td>
                      );
                    } else {
                      const compensation = calculateConsultantCompensation(dataSet);
                      return (
                        <td key={index} className="px-1 py-1 text-sm font-bold text-gray-900 overflow-hidden">
                          {compensation !== null && compensation !== undefined &&
                            new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                              minimumFractionDigits: 2
                            }).format(compensation)
                          }
                        </td>
                      );
                    }
                  })}
                </tr>

              </tbody>
            </table>
          </div>
        </div>
      </DndProvider>
      {!keyDifferences && (
        <div className="flex gap-3 mt-4 justify-center">
          <button
            className="px-6 py-2 bg-blue-600 text-center text-white rounded hover:bg-blue-700 transition disabled:opacity-50"
            onClick={handleCompare}
            disabled={loading}
          >
            {loading ? "Comparing Plans..." : "Compare Plans"}
          </button>
          <button
            className="px-6 py-2 bg-gray-600 text-center text-white rounded hover:bg-gray-700 transition disabled:opacity-50"
            onClick={handleRestore}
            disabled={loading}
          >
            Restore extracted values
          </button>
        </div>
      )}
    </div>
  );
};

export default PlanOrganizer;
